import { useMemo, useId, useEffect, type FC, useContext } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import StandardDropdown from '@wix/da-ds/pkg/Dropdown/Preset/StandardDropdown';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import CheckIcon from '@wix/da-ds/pkg/Icons/Check';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import {
  getVideoCreatorAspectRatio,
  getVideoCreatorDuration,
  getVideoCreatorModel,
  getVideoCreatorSound,
} from '../../../../selectors/studio';
import { useDispatch, useSelector } from 'react-redux';
import { getModelPropertiesByType } from '../../ModelProperties';
import { VideoAspectRatio } from '../../../../types/store';
import {
  updateVideoCreatorAspectRatio,
  updateVideoCreatorDuration,
  updateVideoCreatorSound,
} from '../../../../actions/studio/videoCreator';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import s from './VideoProperties.scss';
import sControl from '../VideoCreatorControls.scss';

export interface Props {}

const VideoProperties: FC<Props> = () => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatch = useDispatch();
  const audioId = useId();
  const selectedModel = useSelector(getVideoCreatorModel);
  const aspectRatio = useSelector(getVideoCreatorAspectRatio);
  const duration = useSelector(getVideoCreatorDuration);
  const hasSound = useSelector(getVideoCreatorSound);

  const model = useMemo(() => {
    return getModelPropertiesByType(selectedModel);
  }, [selectedModel]);
  const aspectRatios = useMemo(() => {
    return model?.availableAspectRatios ?? [];
  }, [model]);
  const durations = useMemo(() => {
    return model?.availableDurations ?? [];
  }, [model]);

  const isValidAspectRatio = useMemo(() => {
    if (!model || !aspectRatio) return false;
    return model.availableAspectRatios.includes(aspectRatio);
  }, [model, aspectRatio]);

  const isValidDuration = useMemo(() => {
    if (!model || !duration) return false;
    return model.availableDurations.includes(duration);
  }, [model, duration]);

  useEffect(() => {
    if (!model) return;

    if (!isValidAspectRatio && model.availableAspectRatios.length > 0) {
      dispatch(
        updateVideoCreatorAspectRatio(
          model.availableAspectRatios[0] as VideoAspectRatio
        )
      );
    }

    if (!isValidDuration && model.availableDurations.length > 0) {
      dispatch(
        updateVideoCreatorDuration(model.availableDurations[0] as number)
      );
    }

    if (!model.hasSound) {
      dispatch(updateVideoCreatorSound(false));
    }
  }, [dispatch, model, isValidAspectRatio, isValidDuration]);

  const displayAspectRatio = useMemo(() => {
    if (!isValidAspectRatio && model?.availableAspectRatios.length > 0) {
      return model.availableAspectRatios[0];
    }
    return aspectRatio;
  }, [isValidAspectRatio, model, aspectRatio]);

  const displayDuration = useMemo(() => {
    if (!isValidDuration && model?.availableDurations.length > 0) {
      return model.availableDurations[0];
    }
    return duration;
  }, [isValidDuration, model, duration]);

  const getAspectRatioBoxSize = (ar: string) => {
    const [w, h] = ar.split(':').map(Number);
    if (!w || !h) return { width: 20, height: 20 };
    if (w < h) {
      return { width: (w * 20) / h, height: 20 };
    } else {
      return { width: 20, height: (h * 20) / w };
    }
  };

  return (
    <div className={cn(s['root'], isMobile && s['mobile'])}>
      <div className={s['aspect']}>
        <label
          className={sControl['control-label']}
          htmlFor="video-creator-prompt"
        >
          {t('studio.videoCreator.label.aspect')}
        </label>
        <div className={s['aspect-dropdown']}>
          <StandardDropdown
            key={`aspect-${model?.id || 'none'}-${displayAspectRatio}`}
            role="menu"
            selectedValue={displayAspectRatio}
            items={aspectRatios.map(ar => ({
              value: ar,
              label: (
                <>
                  <div
                    className={s['aspect-ratio-preview']}
                    style={getAspectRatioBoxSize(ar)}
                  />
                  <span className={s['aspect-label']}>{ar}</span>
                </>
              ),
              complexLabel: (
                <div className={s['aspect-option']}>
                  {ar === displayAspectRatio ? (
                    <div className={s['checked-box']}>
                      <CheckIcon
                        size={IconSize.SMALL}
                        disableDefaultColors
                        className={s['check-icon']}
                      />
                    </div>
                  ) : (
                    <div className={s['unchecked-box']} />
                  )}
                  <div
                    className={s['aspect-ratio-preview']}
                    style={getAspectRatioBoxSize(ar)}
                  />
                  <span className={s['aspect-label']}>{ar}</span>
                </div>
              ),
            }))}
            onSelect={value => {
              dispatch(
                updateVideoCreatorAspectRatio(value as VideoAspectRatio)
              );
            }}
            className={s['dropdown']}
            toggleClassName={s['dropdown-toggle']}
            arrowClassName={s['dropdown-arrow']}
            placement="bottom-end"
          />
        </div>
      </div>

      <div className={s['duration']}>
        <label
          className={sControl['control-label']}
          htmlFor="video-creator-prompt"
        >
          {t('studio.videoCreator.label.duration')}
        </label>
        <div className={s['duration-dropdown']}>
          <StandardDropdown
            key={`duration-${model?.id || 'none'}-${displayDuration}`}
            role="menu"
            selectedValue={displayDuration}
            items={durations.map(d => ({
              value: d,
              label: `${d}s`,
              complexLabel: (
                <div className={s['duration-option']}>
                  {d === displayDuration ? (
                    <div className={s['checked-box']}>
                      <CheckIcon
                        size={IconSize.SMALL}
                        disableDefaultColors
                        className={s['check-icon']}
                      />
                    </div>
                  ) : (
                    <div className={s['unchecked-box']} />
                  )}
                  <span className={s['duration-label']}>{d}s</span>
                </div>
              ),
            }))}
            onSelect={value => {
              dispatch(updateVideoCreatorDuration(value as number));
            }}
            className={s['dropdown']}
            toggleClassName={s['dropdown-toggle']}
            arrowClassName={s['dropdown-arrow']}
            placement="bottom-end"
          />
        </div>
      </div>

      {model?.hasSound !== 'no' && (
        <div className={s['audio']}>
          <label className={cn(s['audio-label'], sControl['control-label'])}>
            {t('studio.videoCreator.label.audio')}
          </label>
          <div className={s['audio-toggle']}>
            {model.hasSound === 'paid' && (
              <>
                <label className={s['audio-detail']} id={audioId}>
                  {t('studio.videoCreator.label.audioDetail', {
                    postProcess: 'reactPostprocessor',
                    modelName: t(`studio.videoCreator.modelName.${model.type}`),
                  })}
                </label>
                <MainToggle
                  className={s['toggle']}
                  variant="main"
                  size={isMobile ? 'large' : 'small'}
                  value={hasSound}
                  onChange={value => {
                    dispatch(updateVideoCreatorSound(value));
                  }}
                  aria-labelledby={audioId}
                />
              </>
            )}
            {model.hasSound === 'free' && (
              <label className={s['audio-detail']} id={audioId}>
                {t('studio.videoCreator.label.audioDetailFree', {
                  postProcess: 'reactPostprocessor',
                  modelName: t(`studio.videoCreator.modelName.${model.type}`),
                })}
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

VideoProperties.displayName = 'VideoProperties';
export default VideoProperties;
